import { createStubTelegramAPI, createTelegramAPI } from '../api/impl/createTelegramAPI';
import type { Fetcher } from '../api/impl/utils/Fetcher';
import { getPurchaseHistory } from '../api/impl/utils/PurchaseHistory';
import type { ReplicantAsyncActionAPI } from '../api/ReplicantAPI';
import { PostAnalyticsFN } from '../common/Analytics';
import { ReplicantABTests } from '../common/ReplicantABTests';
import type { ChatbotMetainfo, default as DB, Entry } from '../db/DB';
import type { AsyncGetters, AsyncGettersAPI } from '../ReplicantAsyncGetters';
import type { Replicant } from '../ReplicantConfig';
import type { ABTestBucket } from '../ReplicantRuleset';
import type { FullSharedState } from '../ReplicantSharedStates';
import type { WithMeta } from '../systemStateFields';
import { deepCopy, getNamespacedValue } from '../utils/ObjUtils';
import type { Namespaced } from '../utils/TypeUtils';
import type { AIClient } from './AIClient';
import type { AtoneClient } from './Atone';
import type { KomojuClient } from './Komoju';
import * as perf from './performanceTracking';
import type { APIAccessInterface, ValidateChatbotSubFn } from './ServerReplicant';
import type { StripeClient } from './Stripe';
import { TelegramClient } from './Telegram';
import { XsollaClient } from './Xsolla';

export type AsyncGetterServerCallableOpts = {
    clockOffset?: number;
    consistentFetchIds: string[];
    dateNow?: () => number;
    getOwnEntry: () => Promise<Entry<WithMeta<any, any, any>>>;
    id: string;
    reportError: ReplicantAsyncActionAPI<any>['reportError'];
    sendAnalyticsEvents: ReplicantAsyncActionAPI<any>['sendAnalyticsEvents'];
    sessionId: string;
};

type AsyncGetterServerCallable = (args: unknown, invokeOpts: AsyncGetterServerCallableOpts) => Promise<unknown>;

export class ServerReplicantAsyncGetters<T extends Replicant> {
    asyncGetters = {} as Namespaced<AsyncGetterServerCallable>;

    constructor(opts: {
        aiClient: AIClient;
        apiAccess: APIAccessInterface<T>;
        atoneClient?: AtoneClient;
        db: DB<any>;
        komojuClient?: KomojuClient;
        telegramClient?: TelegramClient;
        xsollaClient?: XsollaClient;
        stripe?: StripeClient;
        validateChatbotSubscription: ValidateChatbotSubFn;
        sendAnalyticsEvents: PostAnalyticsFN;
        fetcher: Fetcher;
        userAssetsBaseUrl: string;
    }) {
        const asyncGetters: AsyncGetters<any> = opts.apiAccess.config.asyncGetters ?? {};

        const telegramApi = opts.telegramClient
            ? createTelegramAPI({
                  assetUploader: opts.apiAccess.assetUploader,
                  fetcher: opts.fetcher,
                  kvStore: opts.apiAccess.kvStore,
                  telegramClient: opts.telegramClient,
                  userAssetsBaseUrl: opts.userAssetsBaseUrl,
              })
            : createStubTelegramAPI();

        // This is the part of the async getters API that's always the same.
        const asyncGettersApiStatic: Omit<
            AsyncGettersAPI<T>,
            | 'abTests'
            | 'chatbot'
            | 'date'
            | 'fetch'
            | 'fetchStates'
            | 'getOwnPurchaseHistory'
            | 'getOwnState'
            | 'getSessionID'
            | 'getSnapshots'
            | 'getUserAssetUrl'
            | 'getUserID'
            | 'hasPlatformUser'
            | 'payments'
            | 'purchases'
            | 'reportError'
            | 'sendAnalyticsEvents'
            | 'sharedStates'
        > = {
            airtable: {
                getTable: (tableName) => opts.apiAccess.airtable.getTable(tableName),
            },
            searchPlayers: (query) => opts.apiAccess.searchPlayers(query),
            telegram: {
                getProfilePictureUrl: telegramApi.getProfilePictureUrl,
                getChatPictureUrl: telegramApi.getChatPictureUrl,
                createInvoiceLink: telegramApi.createInvoiceLink,
                isSubscribedToChannel: telegramApi.isSubscribedToChannel,
            },
            countPlayers: (query) => opts.apiAccess.countPlayers(query),
            math: { random: () => Math.random() },
            internalKvStore: {
                get: (...args) => opts.apiAccess.internalKvStore.get(...args),
                getBatch: (...args) => opts.apiAccess.internalKvStore.getBatch(...args),
            },
            kvStore: {
                get: (...args) => opts.apiAccess.kvStore.get(...args),
                getBatch: (...args) => opts.apiAccess.kvStore.getBatch(...args),
                send: (...args) => opts.apiAccess.kvStore.send(...args),
                sendBatch: (...args) => opts.apiAccess.kvStore.sendBatch(...args),
            },
            socialGraph: {
                getNonPlayers: (nonPlayerIds) => opts.apiAccess.socialGraph.getNonPlayers(nonPlayerIds),
            },
            ai: {
                completeText: (prompt, completionOpts) => opts.aiClient.textCompletion(prompt, completionOpts),
            },
        };

        function createInvokeAsyncGetterFn(name: string): AsyncGetterServerCallable {
            return (args, invokeOpts) => {
                const {
                    consistentFetchIds,
                    getOwnEntry,
                    id,
                    reportError,
                    sendAnalyticsEvents,
                    sessionId,
                    clockOffset,
                    dateNow,
                } = invokeOpts;

                const date = { now: () => (dateNow?.() ?? opts.apiAccess.dateNow()) + (clockOffset || 0) };

                // Since asyncGetters are read-only we can memoize the getOwnEntry result:
                let getOwnEntryPromise: Promise<Entry<WithMeta<any, any, any>>> | undefined;
                const memoizedGetOwnEntry = () => (getOwnEntryPromise = getOwnEntryPromise ?? getOwnEntry());

                let getOwnChatbotMetainfoPromise: Promise<ChatbotMetainfo | null>;
                const memoizedGetOwnChatbotMetainfo = () =>
                    (getOwnChatbotMetainfoPromise = getOwnChatbotMetainfoPromise ?? opts.db.chatbot.fetchMetainfo(id));

                // Build the full async getters API.
                const asyncGettersApi: AsyncGettersAPI<T> = {
                    ...deepCopy(asyncGettersApiStatic), // Avoid wrapping static API methods twice with performance logging

                    chatbot: {
                        isSubscribed: async () => {
                            if (opts.apiAccess.platform !== 'line') {
                                throw Error(`Operation is not supported on ${opts.apiAccess.platform} platform`);
                            }

                            try {
                                return await opts.validateChatbotSubscription({
                                    db: opts.db,
                                    invokeTime: date.now(),
                                    userId: id,

                                    // Not used by LINE:
                                    entry: {} as any,
                                    isDev: false,
                                    sendChatbotMessage: () => undefined as any,
                                    fetcher: opts.fetcher,
                                    sendAnalyticsEvents: opts.sendAnalyticsEvents,
                                });
                            } catch (error: any) {
                                reportError(error, { userId: id });

                                return false;
                            }
                        },
                    },

                    date,
                    getOwnPurchaseHistory: async () => {
                        const entry = await memoizedGetOwnEntry();

                        return getPurchaseHistory(entry.metainfo, entry.chatbotMetainfo ?? {});
                    },
                    getOwnState: async () =>
                        (await memoizedGetOwnEntry()).state as WithMeta<T['state'], T['ruleset'], T['sharedStates']>,
                    getSessionID: () => sessionId,
                    getSnapshots: () => opts.db.snapshots.fetchSnapshots(id),
                    getUserID: () => id,
                    getUserAssetUrl: (key) => opts.userAssetsBaseUrl + key,
                    fetch: (fetchOpts) => opts.fetcher.fetch(fetchOpts),
                    fetchStates: (ids: string[]) =>
                        opts.apiAccess
                            .fetchStates(ids, { consistentFetchIds, disablePagination: true })
                            .then((res) => res.states),

                    hasPlatformUser: async () => {
                        const { chatbotMetainfo } = await memoizedGetOwnEntry();

                        return chatbotMetainfo?.convertedToUserId !== undefined;
                    },

                    payments: {
                        generateAtonePaymentChecksum: (paymentData) => {
                            if (!opts.atoneClient) {
                                throw Error(
                                    'Atone client is not configured. Make sure to define the ATONE_SECRET_KEY environment variable.',
                                );
                            }

                            return opts.atoneClient.generatePaymentChecksum(paymentData);
                        },

                        getAtoneTransaction: async (transactionId, serviceType) => {
                            if (!opts.atoneClient) {
                                throw Error(
                                    'Atone client is not configured. Make sure to define the ATONE_SECRET_KEY environment variable.',
                                );
                            }

                            return opts.atoneClient.getTransaction(transactionId, serviceType);
                        },

                        getCheckoutSessionStatus: async ({ checkoutSessionId }) => {
                            if (!opts.stripe) {
                                throw Error('Not implemented in offline mode');
                            }

                            const customerId = (await memoizedGetOwnEntry()).metainfo.payments?.customerId;
                            if (!customerId) {
                                throw Error('User has no Stripe customer ID');
                            }

                            const checkoutSession = await opts.stripe.getCheckoutSession({ checkoutSessionId });

                            if (!checkoutSession) {
                                return null;
                            }

                            const { customer, payment_status, status } = checkoutSession;

                            if (!customer || typeof customer !== 'string') {
                                throw Error(
                                    'Invalid customer in Stripe Checkout Session: ' + JSON.stringify(checkoutSession),
                                );
                            }

                            // Client can only read their own Checkout Sessions:
                            if (customer !== customerId) {
                                return null;
                            }

                            if (!status) {
                                throw Error(
                                    'Missing status in Stripe Checkout Session: ' + JSON.stringify(checkoutSession),
                                );
                            }

                            if (payment_status === 'no_payment_required') {
                                throw Error(
                                    'Invalid payment_status in Stripe Checkout session: ' +
                                        JSON.stringify(checkoutSession),
                                );
                            }

                            return { paymentStatus: payment_status, status };
                        },

                        getKomojuPayment: async (paymentId) => {
                            if (!opts.komojuClient) {
                                throw Error(
                                    'Komoju client is not configured. Make sure to define the KOMOJU_SECRET_KEY environment variable.',
                                );
                            }

                            return opts.komojuClient.getPayment(paymentId);
                        },

                        cancelKomojuPayment: async (paymentId) => {
                            if (!opts.komojuClient) {
                                throw Error(
                                    'Komoju client is not configured. Make sure to define the KOMOJU_SECRET_KEY environment variable.',
                                );
                            }

                            return opts.komojuClient.cancelPayment(paymentId);
                        },

                        getXsollaOrder: async ({ token, orderId }) => {
                            if (!opts.xsollaClient) {
                                throw Error(
                                    'Xsolla client is not configured. Make sure to define the XSOLLA_PROJECT_ID and XSOLLA_API_KEY environment variables.',
                                );
                            }
                            try {
                                return await opts.xsollaClient.getOrder({ token, orderId });
                            } catch (error: any) {
                                // report the error, but don't throw it to the game logic
                                reportError(error, { userId: id, orderId });
                                return undefined;
                            }
                        },

                        getPaymentStatus: async ({ paymentIntentId, stripeAccountId }) => {
                            if (!opts.stripe) {
                                throw Error('Not implemented in offline mode');
                            }

                            const paymentIntent = await opts.stripe.getPaymentIntent({
                                expandPaymentMethod: true,
                                paymentIntentId,
                                stripeAccountId,
                            });

                            if (!paymentIntent) {
                                return null;
                            }

                            const paymentMethod = paymentIntent.payment_method;

                            if (typeof paymentMethod === 'string') {
                                throw Error('payment_method failed to expand');
                            }

                            return {
                                creditCardBrand: paymentMethod?.card?.brand,
                                paymentMethod: paymentMethod?.type,
                                paymentStatus: paymentIntent.status,
                                wallet: paymentMethod?.card?.wallet?.type,
                            };
                        },
                    },

                    purchases: {
                        getUnconsumedPurchases: async () => {
                            const chatbotMetainfo = await memoizedGetOwnChatbotMetainfo();

                            const unconsumedProducts: Record<string, number> = {};

                            for (const productId in chatbotMetainfo?.unconsumedProducts) {
                                if (chatbotMetainfo.unconsumedProducts[productId]) {
                                    unconsumedProducts[productId] = chatbotMetainfo.unconsumedProducts[productId];
                                }
                            }

                            return unconsumedProducts;
                        },
                    },

                    reportError, // TODO include user ID in async getter errors
                    sendAnalyticsEvents,

                    sharedStates: Object.keys(opts.apiAccess.config.sharedStates ?? {}).reduce(
                        (a, key) => {
                            const stateName = key as keyof T['sharedStates'] & string;
                            a[stateName] = {
                                fetch: async (stateId) => {
                                    const consistentRead = consistentFetchIds.includes(stateId);

                                    const { sharedState } = await opts.apiAccess.sharedStates.fetchSharedState({
                                        consistentRead,
                                        stateName,
                                        stateId,
                                    });

                                    if (!sharedState) {
                                        return undefined;
                                    }

                                    return sharedState as FullSharedState<any>;
                                },
                                count: (query) => opts.apiAccess.countSharedStates(stateName, query),
                                search: (query) => opts.apiAccess.searchSharedStates(stateName, query),
                            };

                            return a;
                        },
                        {} as AsyncGettersAPI<T>['sharedStates'],
                    ),

                    abTests: {
                        getBucketID: async (testId) => {
                            const [{ metainfo }, { abTests: dynamicConfig }] = await Promise.all([
                                memoizedGetOwnEntry(),
                                opts.apiAccess.abTests.getDynamicConfig(),
                            ]);

                            const abTestsApi = new ReplicantABTests({
                                abTests: opts.apiAccess.config.ruleset?.abTests ?? {},
                                dynamicConfig,
                            });

                            return abTestsApi.getBucketID(metainfo, testId) as
                                | ABTestBucket<NonNullable<T['ruleset']['abTests']>, typeof testId>
                                | undefined;
                        },
                    },
                };

                const isSampled = perf.isSampled();

                const asyncGetter = getNamespacedValue(asyncGetters, name);

                if (!asyncGetter) {
                    throw Error(`Async getter not found in configuration: ${name}`);
                }

                if (isSampled) {
                    const performanceContext: perf.PerformanceContext = { name, type: 'asyncGetter', userId: id };

                    perf.wrapWithPerformanceTracking(asyncGettersApi, performanceContext);

                    const wrappedAsyncGetter = perf.withPerformanceTracking(asyncGetter, performanceContext);

                    return wrappedAsyncGetter(args, asyncGettersApi);
                }

                return asyncGetter(args, asyncGettersApi);
            };
        }

        for (const key in asyncGetters) {
            const asyncGetter = asyncGetters[key];

            if (typeof asyncGetter === 'function') {
                this.asyncGetters[key] = createInvokeAsyncGetterFn(key);
            } else {
                const namespace: Record<string, AsyncGetterServerCallable> = {};

                for (const nestedKey in asyncGetter) {
                    namespace[nestedKey] = createInvokeAsyncGetterFn(`${key}.${nestedKey}`);
                }

                this.asyncGetters[key] = namespace;
            }
        }
    }
}
